import { hasAnalyticsConsent } from './consent';

type Primitive = string | number | boolean;
type EventValue = Primitive | undefined | null | EventObject | EventValue[];
interface EventObject {
  [key: string]: EventValue;
}
type EventParams = EventObject;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const GA_LINKER_DOMAINS = (import.meta.env.VITE_GA_LINKER_DOMAINS as string | undefined)
  ?.split(',')
  .map((d) => d.trim())
  .filter(Boolean);

let isInitialized = false;
let lastTrackedPage = '';

function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
}

function loadGoogleTag(measurementId: string): void {
  if (document.getElementById('ga4-gtag-script')) return;

  const script = document.createElement('script');
  script.id = 'ga4-gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

export function initAnalytics(): boolean {
  if (!hasAnalyticsConsent()) return false;
  if (!GA_MEASUREMENT_ID) return false;
  if (isInitialized) return true;

  ensureDataLayer();
  loadGoogleTag(GA_MEASUREMENT_ID);

  window.gtag?.('js', new Date());

  if (GA_LINKER_DOMAINS && GA_LINKER_DOMAINS.length > 0) {
    window.gtag?.('set', 'linker', { domains: GA_LINKER_DOMAINS });
  }

  window.gtag?.('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    transport_type: 'beacon',
    debug_mode: import.meta.env.DEV,
  });

  isInitialized = true;
  return true;
}

export function trackEvent(eventName: string, params: EventParams = {}): void {
  if (!initAnalytics()) return;
  window.gtag?.('event', eventName, params);
}

export function trackPageView(path: string, title?: string): void {
  if (!initAnalytics()) return;
  if (lastTrackedPage === path) return;
  lastTrackedPage = path;

  window.gtag?.('event', 'page_view', {
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_title: title || document.title,
  });
}

export function trackViewSearchResults(searchTerm: string, params: EventParams = {}): void {
  trackEvent('view_search_results', {
    search_term: searchTerm,
    ...params,
  });
}

export function trackViewItem(item: {
  id: string;
  name: string;
  price?: number;
  category?: string;
  location?: string;
}): void {
  trackEvent('view_item', {
    currency: 'EUR',
    value: item.price,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        item_category: item.category,
        item_location_id: item.location,
      },
    ],
  });
}

export function trackViewItemList(
  listName: string,
  items: Array<{ id: string; name: string; price?: number; category?: string }>
): void {
  trackEvent('view_item_list', {
    item_list_name: listName,
    items: items.map((item, index) => ({
      index,
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      item_category: item.category,
    })),
  });
}

export function trackSelectItem(
  listName: string,
  item: { id: string; name: string; price?: number; category?: string }
): void {
  trackEvent('select_item', {
    item_list_name: listName,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        item_category: item.category,
      },
    ],
  });
}

export function trackBeginCheckout(item: {
  id: string;
  name: string;
  price: number;
  category?: string;
}): void {
  trackEvent('begin_checkout', {
    currency: 'EUR',
    value: item.price,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        item_category: item.category,
        quantity: 1,
      },
    ],
  });
}

export function trackGenerateLead(source: string, value?: number): void {
  const payload: EventParams = {
    lead_source: source,
  };

  if (typeof value === 'number') {
    payload.value = value;
    payload.currency = 'EUR';
  }

  trackEvent('generate_lead', payload);
}

export function trackShare(method: string, contentType: string, itemId?: string): void {
  trackEvent('share', {
    method,
    content_type: contentType,
    item_id: itemId,
  });
}

export function trackContactClick(channel: string, context: string): void {
  trackEvent('contact_click', {
    channel,
    context,
  });
}

export function trackBookingIframe(action: 'open' | 'loaded' | 'error' | 'close' | 'message', params: EventParams = {}): void {
  trackEvent(`booking_iframe_${action}`, params);
}
